import { useState, useMemo } from 'react'
import { Header } from './Header'
import { getCurrentCity } from '@/lib/city'
import type { CityConfig } from '@/lib/city'

type Language = 'el' | 'en'

const LANG_STORAGE_KEY = 'nextbus-language'

// Base translations (city-independent)
const baseTranslations = {
  el: {
    back: '← Πίσω',
    searchPlaceholder: 'Αναζήτηση γραμμών (π.χ. Α3, Χ97...)',
    favorites: 'Αγαπημένα',
    allLines: 'Όλες οι Γραμμές',
    loading: 'Φόρτωση...',
    stopsAway: 'στάσεις μακριά',
    bus: 'Λεωφορείο',
    nowAt: 'Τώρα στη:',
    noBuses: 'Δεν πλησιάζουν λεωφορεία',
    passed: 'ΠΕΡΑΣΕ',
    busesPassed: 'πέρασαν τη στάση',
    otherBuses: 'Άλλα λεωφορεία',
    stops: 'στάσεις',
    getDirections: 'Οδηγίες για τη Στάση',
    refreshEta: 'Ανανέωση ETA',
    min: 'λεπτά',
    official: 'Επίσημο',
    late: 'καθυστέρηση',
    early: 'νωρίτερα',
    copied: 'Αντιγράφηκε!',
    shareText: 'Δες πότε έρχεται το λεωφορείο',
    showMap: 'Χάρτης',
    showList: 'Λίστα',
    neverMissBus: 'Μην χάσεις ποτέ ξανά το λεωφορείο',
    realTimeTracking: 'Παρακολούθηση λεωφορείων σε πραγματικό χρόνο για την Ελλάδα',
    selectCity: 'Επιλέξτε πόλη',
    realTimeETA: 'ETA σε πραγματικό χρόνο',
    busLocations: 'Θέση λεωφορείων',
    favoriteStops: 'Αγαπημένες στάσεις',
    switchToEnglish: 'Switch to English',
    switchToGreek: 'Αλλαγή σε Ελληνικά',
    install: 'Εγκατάσταση',
    installApp: 'Install App',
    share: 'Κοινοποίηση',
    linesTab: 'Γραμμές',
    nearbyTab: 'Κοντά μου',
    locationError: 'Δεν ήταν δυνατή η πρόσβαση στην τοποθεσία σας',
    enableLocation: 'Ενεργοποιήστε την τοποθεσία για να δείτε κοντινές στάσεις',
    nearbyStops: 'Κοντινές Στάσεις',
    metersAway: 'μ',
    noStopsNearby: 'Δεν βρέθηκαν στάσεις κοντά σας',
    popularLines: 'Δημοφιλείς Γραμμές'
  },
  en: {
    back: '← Back',
    searchPlaceholder: 'Search lines (e.g. A3, X97...)',
    favorites: 'Favorites',
    allLines: 'All Lines',
    loading: 'Loading...',
    stopsAway: 'stops away',
    bus: 'Bus',
    nowAt: 'Now at:',
    noBuses: 'No buses approaching',
    passed: 'PASSED',
    busesPassed: 'passed this stop',
    otherBuses: 'Other buses',
    stops: 'stops',
    getDirections: 'Get Directions to Stop',
    refreshEta: 'Refresh ETA',
    min: 'min',
    official: 'Official',
    late: 'late',
    early: 'early',
    copied: 'Copied!',
    shareText: 'Check when the bus arrives',
    showMap: 'Map',
    showList: 'List',
    neverMissBus: 'Never miss the bus again',
    realTimeTracking: 'Real-time bus tracking for Greece',
    selectCity: 'Select your city',
    realTimeETA: 'Real-time ETA',
    busLocations: 'Bus locations',
    favoriteStops: 'Favorite stops',
    switchToEnglish: 'Switch to English',
    switchToGreek: 'Αλλαγή σε Ελληνικά',
    install: 'Εγκατάσταση',
    installApp: 'Install App',
    share: 'Κοινοποίηση',
    linesTab: 'Lines',
    nearbyTab: 'Nearby',
    locationError: 'Unable to access your location',
    enableLocation: 'Enable location to see nearby stops',
    nearbyStops: 'Nearby Stops',
    metersAway: 'm',
    noStopsNearby: 'No stops found nearby',
    popularLines: 'Popular Lines'
  }
}

// City-specific translations (or landing page translations)
function getCityTranslations(city: CityConfig | null, lang: Language) {
  if (!city) {
    // Landing page - generic NextBus
    return {
      title: lang === 'el' ? 'NextBus Ελλάδα' : 'NextBus Greece',
      shareTitle: lang === 'el' ? 'NextBus Ελλάδα' : 'NextBus Greece',
    }
  }
  return {
    title: lang === 'el' ? `NextBus ${city.nameGreek}` : `NextBus ${city.name}`,
    shareTitle: lang === 'el' ? `NextBus ${city.nameGreek}` : `NextBus ${city.name}`,
  }
}

// Combined translations type
type BaseTranslations = typeof baseTranslations['en']
type CityTranslations = ReturnType<typeof getCityTranslations>
type FullTranslations = BaseTranslations & CityTranslations

const translations = baseTranslations

export type Translations = FullTranslations

function loadLanguage(): Language {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY)
    return (stored === 'el' || stored === 'en') ? stored : 'el'
  } catch {
    return 'el'
  }
}

function saveLanguage(lang: Language): void {
  localStorage.setItem(LANG_STORAGE_KEY, lang)
}

export function useAppState() {
  const [lang, setLang] = useState<Language>(loadLanguage)
  const [showCopied, setShowCopied] = useState(false)
  
  // Get current city config
  const city = useMemo(() => getCurrentCity(), [])

  // Combine base translations with city-specific ones
  const t: FullTranslations = useMemo(() => ({
    ...translations[lang],
    ...getCityTranslations(city, lang)
  }), [lang, city])

  const handleShare = async () => {
    const url = window.location.href
    const title = t.shareTitle
    const text = t.shareText

    console.log('Share button clicked, navigator.share available:', !!navigator.share)
    console.log('User agent:', navigator.userAgent)

    if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      try {
        console.log('Using Web Share API')
        await navigator.share({ title, text, url })
      } catch (error) {
        console.log('Web Share API failed or was cancelled:', error)
        // Fall back to clipboard copy
        try {
          await navigator.clipboard.writeText(url)
          setShowCopied(true)
          setTimeout(() => setShowCopied(false), 2000)
        } catch (clipboardError) {
          console.error('Clipboard copy failed:', clipboardError)
        }
      }
    } else {
      console.log('Using clipboard fallback')
      try {
        await navigator.clipboard.writeText(url)
        setShowCopied(true)
        setTimeout(() => setShowCopied(false), 2000)
      } catch (error) {
        console.error('Clipboard copy failed:', error)
      }
    }
  }

  const toggleLanguage = () => {
    const newLang = lang === 'el' ? 'en' : 'el'
    setLang(newLang)
    saveLanguage(newLang)
  }

  return { lang, t, city, showCopied, handleShare, toggleLanguage }
}

interface AppLayoutProps {
  children: React.ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const { lang, t, city, showCopied, handleShare, toggleLanguage } = useAppState()

  return (
    <div className="app">
      <Header
        title={t.title}
        city={city}
        lang={lang}
        onToggleLanguage={toggleLanguage}
        onShare={handleShare}
        showCopied={showCopied}
        copiedText={t.copied}
        t={{
          switchToEnglish: t.switchToEnglish,
          switchToGreek: t.switchToGreek,
          install: t.install,
          installApp: t.installApp,
          share: t.share
        }}
      />
      <main>{children}</main>
    </div>
  )
}

export { translations }
export type { Language }
